import React, { useState } from 'react';
import { X, User, Mail, Phone, Shield, MapPin, GraduationCap, Users, Lock, RefreshCw, Eye, EyeOff, Copy, KeyRound } from 'lucide-react';
import authService from '@/services/authService';
import type { UserCreateRequest, UserSubmitRequest } from '../types';
import {
  buildRolePayload,
  buildTypePayload,
  getAssignableRoles,
  resolveEditableRole,
  type AssignableRole,
} from '../utils/roleUtils';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UserSubmitRequest) => void;
  onResetPassword?: (userId: string, userName: string) => void;
  isLoading?: boolean;
  editingUser?: any;
}

const USER_STATUSES = [
  { value: 'active', label: 'Hoạt động', color: 'text-green-600' },
  { value: 'inactive', label: 'Không hoạt động', color: 'text-gray-600' },
  { value: 'pending_verification', label: 'Chờ xác minh', color: 'text-yellow-600' },
  { value: 'suspended', label: 'Tạm khóa', color: 'text-red-600' }
];

const INSTRUCTOR_TYPES = [
  { value: 'fulltime', label: 'Toàn thời gian' },
  { value: 'parttime', label: 'Bán thời gian' },
  { value: 'guest', label: 'Khách mời' }
];

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onResetPassword,
  isLoading = false,
  editingUser
}) => {
  const currentUser = authService.getCurrentUser();
  const availableRoles = getAssignableRoles(currentUser);

  const [formData, setFormData] = useState<UserCreateRequest>({
    name: editingUser?.name || '',
    email: editingUser?.email || '',
    phone: editingUser?.phone || '',
    user_name: editingUser?.user_name || '',
    password: '',
    role: editingUser ? resolveEditableRole(editingUser) : 'student',
    type: editingUser?.type,
    status: editingUser?.status || 'active',
    unit: editingUser?.unit || '',
    subjects: editingUser?.subjects || [],
    classification: editingUser?.classification || '',
    monthlyQuota: editingUser?.monthlyQuota || 0,
    position: editingUser?.position || '',
    location: editingUser?.location || '',
    instructorType: editingUser?.instructorType || 'fulltime',
    skills: editingUser?.skills || [],
    certifications: editingUser?.certifications || [],
    hourlyRate: editingUser?.hourlyRate || 0
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);

  React.useEffect(() => {
    if (editingUser) {
      setFormData({
        name: editingUser.name || '',
        email: editingUser.email || '',
        phone: editingUser.phone || '',
        user_name: editingUser.user_name || '',
        password: '', // Don't show existing password
        role: resolveEditableRole(editingUser),
        type: editingUser.type,
        status: editingUser.status || 'active',
        unit: editingUser.unit || '',
        subjects: editingUser.subjects || [],
        classification: editingUser.classification || '',
        monthlyQuota: editingUser.monthlyQuota || 0,
        position: editingUser.position || '',
        location: editingUser.location || '',
        instructorType: editingUser.instructorType || 'fulltime',
        skills: editingUser.skills || [],
        certifications: editingUser.certifications || [],
        hourlyRate: editingUser.hourlyRate || 0
      });
    }
  }, [editingUser]);

  const handleInputChange = (field: keyof UserCreateRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleArrayInputChange = (field: keyof UserCreateRequest, value: string) => {
    const arrayValue = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData(prev => ({ ...prev, [field]: arrayValue }));
  };

  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData(prev => ({ ...prev, password }));
  };

  const copyPassword = async () => {
    if (formData.password) {
      try {
        await navigator.clipboard.writeText(formData.password);
        // You could add a toast notification here
      } catch (err) {
        console.error('Failed to copy password:', err);
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Tên không được để trống';
    }

    if (!formData.user_name || !formData.user_name.trim()) {
      newErrors.user_name = 'Tên đăng nhập không được để trống';
    }

    if (formData.email && formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email không hợp lệ';
    }

    if (!formData.role) {
      newErrors.role = 'Vui lòng chọn vai trò';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const selectedRole = formData.role as AssignableRole;
      onSubmit({
        ...formData,
        ...buildRolePayload(selectedRole),
        ...buildTypePayload(selectedRole),
      } as UserSubmitRequest);
    }
  };

  const isTeacher = formData.role === 'teacher' || formData.type === 'teacher';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            {editingUser ? 'Chỉnh sửa người dùng' : 'Tạo người dùng mới'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="inline w-4 h-4 mr-2" />
                Tên *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Nhập tên người dùng"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="inline w-4 h-4 mr-2" />
                Tên đăng nhập *
              </label>
              <input
                type="text"
                value={formData.user_name}
                onChange={(e) => handleInputChange('user_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.user_name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Nhập tên đăng nhập"
              />
              {errors.user_name && <p className="text-red-500 text-sm mt-1">{errors.user_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="inline w-4 h-4 mr-2" />
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nhập email (tùy chọn)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock className="inline w-4 h-4 mr-2" />
                Mật khẩu
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nhập mật khẩu hoặc để trống để tự động tạo"
                />
                <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-2">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="text-gray-400 hover:text-gray-600"
                    title="Tạo mật khẩu ngẫu nhiên"
                  >
                    <RefreshCw size={16} />
                  </button>
                  {formData.password && (
                    <button
                      type="button"
                      onClick={copyPassword}
                      className="text-gray-400 hover:text-gray-600"
                      title="Copy mật khẩu"
                    >
                      <Copy size={16} />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {formData.password ? `Mật khẩu: ${formData.password}` : 'Để trống để hệ thống tự động tạo mật khẩu'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="inline w-4 h-4 mr-2" />
                Số điện thoại
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Nhập số điện thoại"
              />
              {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Shield className="inline w-4 h-4 mr-2" />
                Vai trò *
              </label>
              <select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.role ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {availableRoles.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
              {errors.role && <p className="text-red-500 text-sm mt-1">{errors.role}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trạng thái
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {USER_STATUSES.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>


          {/* Teacher specific fields */}
          {isTeacher && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Thông tin giáo viên
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="inline w-4 h-4 mr-2" />
                    Đơn vị
                  </label>
                  <input
                    type="text"
                    value={formData.unit}
                    onChange={(e) => handleInputChange('unit', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ví dụ: Chi nhánh Hồ Chí Minh"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phân loại
                  </label>
                  <input
                    type="text"
                    value={formData.classification}
                    onChange={(e) => handleInputChange('classification', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ví dụ: Giảng viên, Kiêm nhiệm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vị trí
                  </label>
                  <input
                    type="text"
                    value={formData.position}
                    onChange={(e) => handleInputChange('position', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ví dụ: Giáo viên Toán"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Loại giảng viên
                  </label>
                  <select
                    value={formData.instructorType}
                    onChange={(e) => handleInputChange('instructorType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {INSTRUCTOR_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Môn học (phân cách bằng dấu phẩy)
                  </label>
                  <input
                    type="text"
                    value={formData.subjects?.join(', ')}
                    onChange={(e) => handleArrayInputChange('subjects', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ví dụ: Toán, Lý, Hóa"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kỹ năng (phân cách bằng dấu phẩy)
                  </label>
                  <input
                    type="text"
                    value={formData.skills?.join(', ')}
                    onChange={(e) => handleArrayInputChange('skills', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ví dụ: Giảng dạy, Quản lý lớp"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chứng chỉ (phân cách bằng dấu phẩy)
                  </label>
                  <input
                    type="text"
                    value={formData.certifications?.join(', ')}
                    onChange={(e) => handleArrayInputChange('certifications', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ví dụ: Chứng chỉ sư phạm, IELTS"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tỷ lệ giờ dạy (VNĐ)
                  </label>
                  <input
                    type="number"
                    value={formData.hourlyRate}
                    onChange={(e) => handleInputChange('hourlyRate', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ví dụ: 500000"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Submit buttons */}
          <div className="flex justify-between items-center pt-6 border-t">
            {/* Reset Password button - only show when editing */}
            {editingUser && onResetPassword ? (
              <button
                type="button"
                onClick={() => {
                  const userId = editingUser.id || editingUser.user_name || editingUser.code || '';
                  if (userId) {
                    onResetPassword(userId, editingUser.name);
                  }
                }}
                className="flex items-center px-4 py-2 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-md transition-colors"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Đặt lại mật khẩu
              </button>
            ) : (
              <div></div>
            )}

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Hủy
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Đang lưu...' : (editingUser ? 'Cập nhật' : 'Tạo mới')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;
